import { useCallback, useMemo } from "react"
import InputField from "components/Common/InputField"
import { Form, FormFeedback, Label } from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import FormikCreatableSelect from "components/Common/FormikCreatableSelect"

const CustomerForm = ({
  // Form data
  initialData = {},

  // Options for dropdowns
  options = {},

  // Form control
  onSubmit,
  onCancel,
  isEdit = false,
  isLoading = false,

  // UI customization
  showAdditionalInfo = true,
  submitButtonText,
  cancelButtonText = "Cancel",
}) => {
  // Destructure options with fallbacks
  const {
    designations = [],
    departments = [],
    companies = [],
    customerTypes = [],
    areas = [],
    cities = [],
  } = options

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required("Please enter customer name"),
    mobile: Yup.string().max(20, "Mobile must be at most 20 characters"),
    email: Yup.string().email("Invalid email format").max(255),
    designation_id: Yup.string().nullable(),
    department_id: Yup.string().nullable(),
    company_id: Yup.string().nullable(),
    customer_type_id: Yup.string().nullable(),
    area_id: Yup.string().nullable(),
    city_id: Yup.string().nullable(),
    full_address: Yup.string().max(1000),
  })

  // Formik setup
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: initialData?.name || "",
      mobile: initialData?.mobile || "",
      email: initialData?.email || "",
      designation_id: initialData?.designation_id || "",
      department_id: initialData?.department_id || "",
      company_id: initialData?.company_id || "",
      customer_type_id: initialData?.customer_type_id || "",
      area_id: initialData?.area_id || "",
      city_id: initialData?.city_id || "",
      full_address: initialData?.full_address || "",
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: (values, formikHelpers) => {
      onSubmit(values, formikHelpers)
    },
  })

  // Default submit button text
  const defaultSubmitText = useMemo(() => {
    return `${isEdit ? "Save Changes" : "Add Customer"}${
      isLoading ? "..." : ""
    }`
  }, [isEdit, isLoading])

  const handleSave = useCallback(() => {
    formik.handleSubmit()
  }, [formik])

  return (
    <Form>
      {/* Name */}
      <div className="mb-3">
        <Label htmlFor="name">
          Name <span className="text-danger">*</span>
        </Label>
        <InputField
          type="text"
          name="name"
          id="name"
          value={formik.values.name}
          onChange={formik.handleChange}
          invalid={formik.touched.name && !!formik.errors.name}
        />
        {formik.touched.name && formik.errors.name && (
          <FormFeedback type="invalid">{formik.errors.name}</FormFeedback>
        )}
      </div>

      {/* Mobile */}
      <div className="mb-3">
        <Label htmlFor="mobile">Mobile</Label>
        <InputField
          type="text"
          name="mobile"
          id="mobile"
          value={formik.values.mobile}
          onChange={formik.handleChange}
          invalid={formik.touched.mobile && !!formik.errors.mobile}
        />
        {formik.touched.mobile && formik.errors.mobile && (
          <FormFeedback type="invalid">{formik.errors.mobile}</FormFeedback>
        )}
      </div>

      {/* Email */}
      <div className="mb-3">
        <Label htmlFor="email">Email</Label>
        <InputField
          type="email"
          name="email"
          id="email"
          value={formik.values.email}
          onChange={formik.handleChange}
          invalid={formik.touched.email && !!formik.errors.email}
        />
        {formik.touched.email && formik.errors.email && (
          <FormFeedback type="invalid">{formik.errors.email}</FormFeedback>
        )}
      </div>

      {/* Full Address */}
      <div className="mb-3">
        <Label htmlFor="full_address">Full Address</Label>
        <InputField
          type="textarea"
          name="full_address"
          id="full_address"
          value={formik.values.full_address}
          onChange={formik.handleChange}
          invalid={formik.touched.full_address && !!formik.errors.full_address}
        />
        {formik.touched.full_address && formik.errors.full_address && (
          <FormFeedback type="invalid">
            {formik.errors.full_address}
          </FormFeedback>
        )}
      </div>

      {showAdditionalInfo && (
        <>
          <div className="mb-3 border-bottom">Additional Info</div>

          {/* Dropdowns */}
          <FormikCreatableSelect
            name="designation_id"
            label="Designation"
            formik={formik}
            options={designations.map(d => ({ value: d.id, label: d.name }))}
            placeholder="Select or type a designation"
          />

          <FormikCreatableSelect
            name="department_id"
            label="Department"
            formik={formik}
            options={departments.map(d => ({ value: d.id, label: d.name }))}
            placeholder="Select or type a department"
          />

          <FormikCreatableSelect
            name="company_id"
            label="Company"
            formik={formik}
            options={companies.map(c => ({ value: c.id, label: c.name }))}
            placeholder="Select or type a company"
          />

          <FormikCreatableSelect
            name="customer_type_id"
            label="Customer Type"
            formik={formik}
            options={customerTypes.map(ct => ({
              value: ct.id,
              label: ct.name,
            }))}
            placeholder="Select or type a customer type"
          />

          <FormikCreatableSelect
            name="area_id"
            label="Area"
            formik={formik}
            options={areas.map(a => ({ value: a.id, label: a.name }))}
            placeholder="Select or type an area"
          />

          <FormikCreatableSelect
            name="city_id"
            label="City"
            formik={formik}
            options={cities.map(c => ({ value: c.id, label: c.name }))}
            placeholder="Select or type a city"
          />
        </>
      )}

      {/* Action Buttons */}
      <div className="text-end mt-4 pt-3 border-top">
        {onCancel && (
          <button
            type="button"
            className="btn btn-secondary me-2"
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancelButtonText}
          </button>
        )}
        <button type="button" onClick={handleSave} className="btn btn-success" disabled={isLoading}>
          {submitButtonText || defaultSubmitText}
        </button>
      </div>
    </Form>
  )
}

export default CustomerForm
